import {
  type ColorToken,
  DonutChart,
  type DonutSegment,
  LegendRow,
  StackedBar,
  type StackedBarSegment,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import {
  type LifeReadinessResponse,
  type ReadinessCategory,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import { Box, type SxProps, type Theme, Typography } from '@mui/material';

import {
  bucketCount,
  completionStatusLabel,
  humanize,
  percentOf,
  percentShares,
  readinessBandLabel,
} from './analytics.labels';
import { pieLegendSx, pieRowSx } from './analytics.styles';
import { AnalyticsCard } from './AnalyticsCard';
import { SubsectionHeader } from './AnalyticsSubsectionHeader';
import { useAgentContext } from './LifeReadinessCards.agentContext';

const { lifeReadiness } = appStrings.modules.coachingAnalytics;

const RUNG_SEGMENTS: readonly {
  key: string;
  label: string;
  color: ColorToken;
  labelColor: ColorToken;
}[] = (
  [
    { key: 'not_yet', color: 'Gray/200', labelColor: 'Colors/Text/text-primary' },
    { key: 'early', color: 'BlueDark/300', labelColor: 'Colors/Base/White' },
    { key: 'strong', color: 'BlueDark/500', labelColor: 'Colors/Base/White' },
    { key: 'advanced', color: 'Gray/800', labelColor: 'Colors/Base/White' },
  ] as const
).map((rung) => ({ ...rung, label: readinessBandLabel(rung.key) }));

const CATEGORY_BAR_HEIGHT = 24;

const COMPLETION_STATUSES: readonly { id: string; color: ColorToken }[] = [
  { id: 'completed', color: 'Blue/800' },
  { id: 'in_progress', color: 'Blue/500' },
  { id: 'not_started', color: 'Blue/100' },
];

const PIE_SIZE = 150;

const CompletionCard = ({ data }: Readonly<{ data: LifeReadinessResponse }>) => {
  const { completion } = data;
  const shares = percentShares(
    COMPLETION_STATUSES.map(
      (status) => completion.statuses.find((row) => row.id === status.id)?.count ?? 0,
    ),
    completion.total,
  );
  const rows = COMPLETION_STATUSES.map((status, index) => ({
    ...status,
    label: completionStatusLabel(status.id),
    pct: shares[index],
  }));
  const segments: DonutSegment[] = rows.map((row) => ({
    label: row.label,
    value: row.pct,
    color: row.color,
  }));

  return (
    <AnalyticsCard
      title={lifeReadiness.completion.title}
      subtitle={lifeReadiness.completion.subtitle(data.students.total)}
      info={[lifeReadiness.completion.caveat(completion.questions.visible)]}
    >
      <Box sx={pieRowSx}>
        <DonutChart
          variant={{ type: 'multi', segments }}
          size={PIE_SIZE}
          cutout={0}
          isTooltipEnabled={false}
        />
        <Box sx={pieLegendSx}>
          {rows.map((row) => (
            <LegendRow key={row.id} color={row.color} label={row.label} pct={row.pct} />
          ))}
        </Box>
      </Box>
    </AnalyticsCard>
  );
};

const ReadinessCategoryBar = ({ row }: Readonly<{ row: ReadinessCategory }>) => {
  const segments: StackedBarSegment[] = RUNG_SEGMENTS.map((segment) => ({
    key: segment.key,
    pct: percentOf(bucketCount(row.answers.bands, segment.key), row.answers.total),
    color: segment.color,
    labelColor: segment.labelColor,
  }));

  const coverage = lifeReadiness.byCategory.coverage(
    row.coverage.answered,
    row.coverage.answerable,
  );

  return (
    <Box sx={categoryRowSx}>
      <Box sx={categoryHeaderSx}>
        <Typography variant="text-xs-medium" sx={categoryLabelSx}>
          {row.name ?? humanize(String(row.id))}
        </Typography>
        <Typography variant="text-xs-regular" sx={categoryLabelSx}>
          {coverage}
        </Typography>
      </Box>
      <StackedBar segments={segments} height={CATEGORY_BAR_HEIGHT} />
    </Box>
  );
};

const ByCategoryCard = ({ data }: Readonly<{ data: LifeReadinessResponse }>) => {
  const { readiness_by_category: readiness } = data;

  return (
    <AnalyticsCard
      title={lifeReadiness.byCategory.title}
      subtitle={lifeReadiness.byCategory.subtitle(readiness.students_started)}
      info={[lifeReadiness.byCategory.caveat]}
    >
      <Box sx={legendSx}>
        {RUNG_SEGMENTS.map((segment) => (
          <LegendRow key={segment.key} color={segment.color} label={segment.label} />
        ))}
      </Box>
      <Box sx={categoryListSx}>
        {readiness.categories.map((row) => (
          <ReadinessCategoryBar key={row.id} row={row} />
        ))}
      </Box>
    </AnalyticsCard>
  );
};

export const LifeReadinessSection = ({ data }: Readonly<{ data: LifeReadinessResponse }>) => {
  useAgentContext(data);

  return (
    <Box sx={sectionSx}>
      <SubsectionHeader
        title={lifeReadiness.title}
        subtitle={lifeReadiness.subtitle}
        action={
          <LubavButton
            id="coaching-analytics-life-readiness-discuss-lubav"
            label={appStrings.modules.coachingAnalytics.successSystem.discussWithLubav}
            promptButtonId="coaching_analytics_life_readiness"
          />
        }
      />
      <Box sx={gridSx}>
        <CompletionCard data={data} />
        <ByCategoryCard data={data} />
      </Box>
    </Box>
  );
};

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  width: '100%',
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: backrs.spacing['spacing-3xl'],
  alignItems: 'stretch',
  '@container (min-width: 900px)': {
    gridTemplateColumns: 'minmax(0, 0.75fr) minmax(0, 1.25fr)',
  },
});

const legendSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: backrs.spacing['spacing-xl'],
  width: '100%',
});

const categoryListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const categoryRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
  width: '100%',
});

const categoryHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
  width: '100%',
});

const categoryLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
